// Timezone helpers — the yacht sails across timezones, so we store everything
// in UTC and render in the currently-configured IANA display zone. Bookings
// that have already ended keep their original `booking_tz` snapshot so past
// history reads as it did on the day it happened.

import { useEffect, useState, useCallback } from "react";
import { api } from "./api";

// NON-SENSITIVE UI preference — cached IANA display timezone so the UI
// doesn't flash the default while /public/settings loads. Never contains
// credentials or session data.
const CACHE_KEY = "yachtspa_tz";

let _appTz = null;
const _listeners = new Set();

export function getAppTz() {
  if (_appTz) return _appTz;
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) _appTz = cached;
  } catch {}
  return _appTz || "UTC";
}

export function setAppTz(tz) {
  _appTz = tz || "UTC";
  try { localStorage.setItem(CACHE_KEY, _appTz); } catch {}
  _listeners.forEach((fn) => { try { fn(_appTz); } catch {} });
}

export function subscribeAppTz(fn) {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

export async function refreshAppTz() {
  try {
    const { data } = await api.get("/public/settings");
    setAppTz(data?.timezone || "UTC");
    return _appTz;
  } catch {
    return getAppTz();
  }
}

/** React hook — always renders with the current app timezone; auto-updates when it changes. */
export function useAppTz() {
  const [tz, setTz] = useState(getAppTz());
  useEffect(() => {
    const unsub = subscribeAppTz(setTz);
    if (!_appTz) refreshAppTz();
    return () => { unsub(); };
  }, []);
  return tz;
}

/** Format an ISO datetime in the given (or current-app) timezone. */
export function formatInTz(iso, opts = {}, tz = null) {
  if (!iso) return "";
  const zone = tz || getAppTz();
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: zone,
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      ...opts,
    }).format(d);
  } catch {
    return iso;
  }
}

/** "Wed 14 Feb · 14:00" style label — used on booking cards / calendar tooltips. */
export function fmtDateTime(iso, tz = null) {
  return formatInTz(iso, { weekday: "short", day: "2-digit", month: "short", year: "numeric" }, tz)
    + " · "
    + formatInTz(iso, { hour: "2-digit", minute: "2-digit", hour12: false }, tz);
}

export function fmtTime(iso, tz = null) {
  return formatInTz(iso, { hour: "2-digit", minute: "2-digit", hour12: false }, tz);
}

export function fmtDate(iso, tz = null) {
  return formatInTz(iso, { weekday: "short", day: "2-digit", month: "short", year: "numeric" }, tz);
}

/**
 * Pick the right zone for a booking:
 *  - Ended / past bookings → the frozen `booking_tz` snapshot (or app zone as fallback).
 *  - Upcoming / active bookings → current app timezone.
 */
export function bookingTz(booking, now = new Date()) {
  const end = booking?.end_at ? new Date(booking.end_at) : null;
  if (end && end < now) return booking?.booking_tz || getAppTz();
  return getAppTz();
}

/** Short IANA zone abbreviation, e.g. "CET" or "EEST" for the current instant. */
export function tzAbbr(tz = null, iso = null) {
  const zone = tz || getAppTz();
  try {
    const d = iso ? new Date(iso) : new Date();
    const parts = new Intl.DateTimeFormat("en-GB", { timeZone: zone, timeZoneName: "short" }).formatToParts(d);
    return (parts.find((p) => p.type === "timeZoneName") || {}).value || zone;
  } catch { return zone; }
}

/** All IANA zones the browser knows about (fallback to a curated list on old browsers). */
export function listIanaZones() {
  try {
    if (typeof Intl.supportedValuesOf === "function") return Intl.supportedValuesOf("timeZone");
  } catch {}
  return [
    "UTC",
    "Europe/London", "Europe/Paris", "Europe/Athens", "Europe/Monaco",
    "Europe/Rome", "Europe/Madrid", "Europe/Amsterdam", "Europe/Zurich",
    "America/New_York", "America/Los_Angeles", "America/Miami",
    "Asia/Dubai", "Asia/Singapore", "Asia/Tokyo", "Australia/Sydney",
  ];
}

/**
 * Return the "GMT±HH:MM" offset for an IANA zone at the given instant.
 * Uses `Intl.DateTimeFormat`'s `longOffset` token which returns strings like
 * "GMT+02:00" / "GMT-05:00" / "GMT" (for zero). Falls back to the offset of
 * `new Date()` if the token isn't supported.
 */
export function gmtOffsetFor(tz, when = new Date()) {
  try {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: tz, timeZoneName: "longOffset",
    }).formatToParts(when);
    const raw = parts.find((p) => p.type === "timeZoneName")?.value || "GMT";
    // Some engines return "GMT" (no offset) for UTC — normalise to +00:00.
    if (raw === "GMT") return "GMT+00:00";
    // Others return "GMT+2" instead of "GMT+02:00" — pad if needed.
    const m = /^GMT([+-])(\d{1,2})(?::?(\d{2}))?$/.exec(raw);
    if (m) {
      const sign = m[1]; const h = m[2].padStart(2, "0"); const mm = m[3] || "00";
      return `GMT${sign}${h}:${mm}`;
    }
    return raw;
  } catch {
    return "GMT+00:00";
  }
}

/**
 * Build a grouped list of IANA zones for a `<select>` menu — each row is
 * `{ gmt: "GMT+02:00", offsetMin: 120, zones: [{ tz, city, region }, ...] }`
 * with the group's city examples pre-computed. Sorted by numeric offset
 * (west → east) so operators see the world at a glance.
 *
 * Passed a `when` Date so DST-affected zones bucket correctly at "now".
 */
export function buildTimezoneGroups(when = new Date()) {
  const zones = listIanaZones();
  const byGmt = new Map();
  for (const tz of zones) {
    const gmt = gmtOffsetFor(tz, when);
    // Skip zones without a slash (e.g. "UTC", "Etc/*") for the grouped
    // display but keep UTC as a first-class option below.
    const parts = tz.split("/");
    const region = parts.length > 1 ? parts[0] : "Etc";
    const city = (parts[parts.length - 1] || tz).replace(/_/g, " ");
    const row = byGmt.get(gmt) || { gmt, offsetMin: _offsetToMinutes(gmt), zones: [] };
    row.zones.push({ tz, city, region });
    byGmt.set(gmt, row);
  }
  const groups = Array.from(byGmt.values()).sort((a, b) => a.offsetMin - b.offsetMin);
  // Sort zones inside each group by city name so the operator can scan.
  for (const g of groups) g.zones.sort((a, b) => a.city.localeCompare(b.city));
  return groups;
}

function _offsetToMinutes(gmt) {
  const m = /^GMT([+-])(\d{2}):(\d{2})$/.exec(gmt || "GMT+00:00");
  if (!m) return 0;
  const sign = m[1] === "-" ? -1 : 1;
  return sign * (Number(m[2]) * 60 + Number(m[3]));
}

/**
 * Short city-example label for a GMT offset group — used in the summary
 * chip below the dropdown so the operator can double-check the pick.
 * Up to 3 cities, comma-separated. e.g. "Athens, Cairo, Helsinki".
 */
export function cityExamplesFor(groupZones, limit = 3) {
  const preferred = ["Athens", "London", "Paris", "New York", "Los Angeles",
                     "Dubai", "Singapore", "Tokyo", "Sydney", "Auckland",
                     "Reykjavik", "Cairo", "Helsinki", "Moscow", "Karachi",
                     "Mumbai", "Bangkok", "Hong Kong", "Honolulu", "Anchorage"];
  const preferredHit = groupZones
    .map((z) => z.city)
    .filter((c) => preferred.includes(c))
    .slice(0, limit);
  if (preferredHit.length >= limit) return preferredHit.join(", ");
  const rest = groupZones.map((z) => z.city).filter((c) => !preferredHit.includes(c));
  return [...preferredHit, ...rest].slice(0, limit).join(", ");
}

export function useTzAbbrRefresher() {
  // Small helper for components that want to render tz abbreviations that
  // auto-refresh when the app tz changes.
  const tz = useAppTz();
  return useCallback((iso) => tzAbbr(tz, iso), [tz]);
}

/**
 * Return the "YYYY-MM-DD" bucket key of an ISO instant in the given zone.
 *
 * Fixes the Spa iPad calendar bug where a booking stored as
 * `2026-08-21T00:00:00+02:00` would land in Friday 21's column via the naive
 * `iso.slice(0, 10)` day-bucket, while `new Date(...).getHours()` placed the
 * tile at 22:00 (browser-local), and the dialog's `fmtDateTime(booking_tz)`
 * showed "Thu 20 Aug · 22:00" — three inconsistent reference frames.
 * This helper anchors ALL of them to a single IANA zone (default: the app
 * timezone) so day-bucketing, hour positioning and the dialog display agree.
 */
export function dayKeyInTz(iso, tz = null) {
  if (!iso) return "";
  const zone = tz || getAppTz();
  try {
    // en-CA uses the "YYYY-MM-DD" ISO ordering natively for `formatToParts`,
    // and Intl.DateTimeFormat is timezone-aware — the ideal primitive.
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: zone, year: "numeric", month: "2-digit", day: "2-digit",
    }).formatToParts(new Date(iso));
    const y = parts.find((p) => p.type === "year")?.value;
    const m = parts.find((p) => p.type === "month")?.value;
    const d = parts.find((p) => p.type === "day")?.value;
    return `${y}-${m}-${d}`;
  } catch {
    return String(iso).slice(0, 10);
  }
}

/**
 * Return `{ hour, minute }` (numbers) of an ISO instant in the given zone.
 * Used by the calendar's tile positioning so the vertical offset matches the
 * day column the tile is placed in.
 */
export function hmInTz(iso, tz = null) {
  if (!iso) return { hour: 0, minute: 0 };
  const zone = tz || getAppTz();
  try {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: zone, hour: "2-digit", minute: "2-digit", hour12: false,
    }).formatToParts(new Date(iso));
    const h = parts.find((p) => p.type === "hour")?.value ?? "0";
    const m = parts.find((p) => p.type === "minute")?.value ?? "0";
    // Intl sometimes returns "24" for midnight — coerce back to 0.
    const hh = Number(h) % 24;
    return { hour: hh, minute: Number(m) };
  } catch {
    const d = new Date(iso);
    return { hour: d.getHours(), minute: d.getMinutes() };
  }
}

/**
 * Return the UTC ISO string for a WALL-CLOCK `hour:minute` on the day
 * represented by `day` (a Date whose calendar day we care about IN `tz`,
 * not in the browser's tz). This is the inverse of `hmInTz`.
 *
 * Solves the calendar drag/drop "jumps by tz offset" bug: dragging a
 * booking onto the "10:00" row of a yacht-tz calendar was constructing
 * the target ISO with `new Date(day).setHours(h, m)` — which sets the
 * hour in the BROWSER'S tz. Admins on UTC would then see the moved
 * booking land at 13:00 EEST on a yacht in Europe/Athens.
 */
export function zonedIsoFromWallTime(day, hour, minute, tz = null) {
  const zone = tz || getAppTz();
  const iso = day instanceof Date ? day.toISOString() : new Date(day).toISOString();
  const dayKey = dayKeyInTz(iso, zone); // "YYYY-MM-DD" in the target zone.
  const [y, m, d] = dayKey.split("-").map(Number);
  // First guess: interpret the desired wall-clock as if it were already UTC.
  const guess = new Date(Date.UTC(y, (m || 1) - 1, d || 1, hour, minute, 0));
  const shown = hmInTz(guess.toISOString(), zone);
  const wantMin = hour * 60 + minute;
  const shownMin = shown.hour * 60 + shown.minute;
  let deltaMin = wantMin - shownMin;
  // Wrap around the day boundary — a +14h "correction" is really a -10h one.
  if (deltaMin > 720) deltaMin -= 1440;
  if (deltaMin < -720) deltaMin += 1440;
  return new Date(guess.getTime() + deltaMin * 60000).toISOString();
}
